import { useState, useEffect } from "react";
import "./CRM.css"; // Reuse broad styles
import "./Sales&Orders.css";
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { generateInvoicePDF } from "../utils/invoiceGenerator";
import { formatCurrency } from "../utils/formatCurrency";
import { 
  Eye, 
  FileEdit, 
  Trash2, 
  MessageCircle,
  Plus,
  Search,
  FileDown,
  Download
} from "lucide-react";

function SalesOrdersScreen() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await dataService.getOrders("Sales Order");
      setOrders(response.data.data);
    } catch (error) {
      console.error("Failed to fetch orders", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleCreateSales = () => {
    navigate("/sales/create");
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const idIdx = headers.findIndex(h => h.toLowerCase().includes('order') || h.toLowerCase().includes('id') || h.toLowerCase().includes('number'));
      const customerIdx = headers.findIndex(h => h.toLowerCase().includes('customer') || h.toLowerCase().includes('client'));
      const dateIdx = headers.findIndex(h => h.toLowerCase().includes('date'));
      const amountIdx = headers.findIndex(h => h.toLowerCase().includes('amount') || h.toLowerCase().includes('total'));
      const productIdx = headers.findIndex(h => h.toLowerCase().includes('product') || h.toLowerCase().includes('item'));
      const qtyIdx = headers.findIndex(h => h.toLowerCase().includes('quantity') || h.toLowerCase().includes('qty'));
      const priceIdx = headers.findIndex(h => h.toLowerCase().includes('price') || h.toLowerCase().includes('rate'));
      const statusIdx = headers.findIndex(h => h.toLowerCase().includes('status'));
      const phoneIdx = headers.findIndex(h => h.toLowerCase().includes('phone') || h.toLowerCase().includes('contact'));

      const newOrders = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        const amountVal = parseFloat((row[amountIdx] || '0').replace(/"/g, '').trim()) || 0;
        const qtyVal = parseInt((row[qtyIdx] || '1').replace(/"/g, '').trim(), 10) || 1;
        const priceVal = parseFloat((row[priceIdx] || '0').replace(/"/g, '').trim()) || 0;
        const prodName = (row[productIdx] || 'General Sale Item').replace(/"/g, '').trim();

        newOrders.push({
          orderId: (row[idIdx] || `SO-${Date.now().toString().slice(-4)}-${i}`).replace(/"/g, '').trim(),
          customer: (row[customerIdx] || 'Imported Customer').replace(/"/g, '').trim(),
          customerPhone: (row[phoneIdx] || '').replace(/"/g, '').trim(),
          date: (row[dateIdx] || new Date().toISOString().split('T')[0]).replace(/"/g, '').trim(),
          amount: amountVal || (qtyVal * priceVal),
          items: [{
            product: prodName,
            quantity: qtyVal,
            price: priceVal || (amountVal / qtyVal),
            total: amountVal || (qtyVal * priceVal)
          }],
          status: (row[statusIdx] || 'Pending').replace(/"/g, '').trim(),
          type: "Sales Order",
          discount: 0
        });
      }

      if (newOrders.length > 0) {
        addToast(`Importing ${newOrders.length} sales orders...`, "success");
        try {
          for (const order of newOrders) {
            await dataService.addOrder(order);
          }
          fetchOrders();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadQuotation = (order) => {
    generateInvoicePDF(order, "Quotation");
    addToast("Quotation downloaded successfully", "success");
  };

  const handleView = (order) => {
    setSelectedOrder(order);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (order) => {
    navigate(`/sales/edit/${order._id}`);
  };

  const handleDeleteClick = (order) => {
    setItemToDelete(order);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dataService.deleteOrder(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchOrders();
      addToast("Order deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete order: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dataService.updateOrder(selectedOrder._id, formData);
      setIsModalOpen(false);
      fetchOrders();
      addToast("Order updated successfully", "success");
    } catch (error) {
      addToast("Failed to update order: " + error.message, "error");
    }
  };

  const handleWhatsAppFollowUp = (order) => {
    if (!order.customerPhone) {
      addToast("No phone number found for this customer", "error");
      return;
    }
    const phone = order.customerPhone.replace(/\D/g, '');
    const message = `Hello, this is a follow-up regarding your Sales Order #${order.orderId} for ${formatCurrency(order.amount)}. We wanted to update you on the status. Thank you!`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Sales & Orders</h1>
          <p>Manage customer orders and pricing</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission('sales', 'createSalesOrder') && (
            <button className="primary-btn" onClick={handleCreateSales}>
              <Plus size={18} /> Create Sales Order
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Orders</span>
            <h2 className="stat-value">{orders.length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Revenue</span>
            <h2 className="stat-value">{formatCurrency(orders.reduce((acc, o) => acc + o.amount, 0))}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Completed</span>
            <h2 className="stat-value" style={{ color: '#00A859' }}>{orders.filter(o => o.status === 'Completed').length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Pending</span>
            <h2 className="stat-value" style={{ color: '#EA580C' }}>{orders.filter(o => o.status === 'Pending').length}</h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Sales Orders</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by ID or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading orders...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Discount</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders
                  .filter(order =>
                    order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (order.customer && order.customer.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .map((order) => (
                    <tr key={order._id}>
                      <td><span className="font-bold">{order.orderId}</span></td>
                      <td>{order.customer || "N/A"}</td>
                      <td>{new Date(order.date).toLocaleDateString()}</td>
                      <td>{order.items?.length || 0}</td>
                      <td>{order.discount}</td>
                      <td><span className="font-bold">{formatCurrency(order.amount)}</span></td>
                      <td>
                        <span className={`status-pill ${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-icons">
                          <button className="icon-btn" title="View" onClick={() => handleView(order)}><Eye size={16} /></button>
                          
                          <button 
                            className="icon-btn" 
                            style={{ color: '#2E7D32' }} 
                            title="WhatsApp Follow-up" 
                            onClick={() => handleWhatsAppFollowUp(order)}
                          >
                            <MessageCircle size={16} />
                          </button>

                          {authService.hasPermission('sales', 'editSalesOrder') && (
                            <button className="icon-btn success" title="Edit" onClick={() => handleEdit(order)}><FileEdit size={16} /></button>
                          )}

                          <button className="icon-btn" title="Download Quotation" onClick={() => handleDownloadQuotation(order)}><FileDown size={16} /></button>

                          {authService.hasPermission('sales', 'deleteSalesOrder') && (
                            <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteClick(order)}><Trash2 size={16} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>No sales orders found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Sales Order Details" : "Edit Sales Order"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
          )
        }
      >
        {modalMode === "view" && selectedOrder && (
          <div className="detail-view">
            <div className="detail-item">
              <span className="detail-label">Order ID</span>
              <span className="detail-value">{selectedOrder.orderId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Customer</span>
              <span className="detail-value">{selectedOrder.customer}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Date</span>
              <span className="detail-value">{new Date(selectedOrder.date).toLocaleDateString()}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Items</span>
              <div className="detail-value">
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {selectedOrder.items?.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6' }}>
                      {item.product} - {item.quantity} x {formatCurrency(item.price)} = {formatCurrency(item.total)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="detail-item">
              <span className="detail-label">Total Amount</span>
              <span className="detail-value" style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(selectedOrder.amount)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-pill ${selectedOrder.status?.toLowerCase()}`}>
                {selectedOrder.status}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Warehouse</span>
              <span className="detail-value">{selectedOrder.warehouse || 'Main Warehouse - Dubai'}</span>
              <div className="detail-item" style={{ marginTop: '20px' }}>
                <button className="primary-btn" onClick={() => handleDownloadQuotation(selectedOrder)}>
                  Download Quotation
                </button>
              </div>
            </div>
          </div>
        )}

        {modalMode === "edit" && (
          <form className="modal-form" onSubmit={handleUpdateSubmit}>
            <div className="form-group">
              <label>Order ID</label>
              <input name="orderId" value={formData.orderId || ""} readOnly disabled />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status || ""} onChange={handleFormChange}>
                <option value="Pending">Pending</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Shipped">Shipped</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            {/* Warehouse hidden as requested - driven by product selection */}
            <div className="form-group" style={{ display: 'none' }}>
              <label>Warehouse</label>
              <select name="warehouse" value={formData.warehouse || "Main Warehouse - dubai"} onChange={handleFormChange}>
                <option value="Main Warehouse - dubai">Main Warehouse - Dubai</option>
                <option value="West Coast Hub - sharjah">West Coast Hub - Sharjah</option>
              </select>
            </div>
            <div className="form-group">
              <label>Total Amount Override</label>
              <input type="number" name="amount" value={formData.amount || 0} onChange={handleFormChange} />
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Order</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete sales order <strong>{itemToDelete?.orderId}</strong>. This action cannot be undone.</p>
        </div>
      </Modal>
    </div>
  );
}

export default SalesOrdersScreen;
